// OGC-531 Creatives Manager v19 (platform admin Creative Catalog). Screens
// share one route, #/platform/catalog, selected by the `screen` query value:
// the catalog (default), Renovation types and the full-page creative editor
// (`screen=edit&creative=<id|new>`). The catalog's filters stay in the query
// so returning from another screen restores them.
import { useEffect, useMemo, useRef, useState } from 'react';
import { replaceHash } from '../../shell/router.js';
import { useManagementQuery } from '../PlatformManagement.jsx';
import CreativeEditor from './CreativeEditor.jsx';
import CreativesCatalog from './CreativesCatalog.jsx';
import RenovationTypesManager from './RenovationTypesManager.jsx';
import { createCreativesApi } from './creatives-api.js';
import './CreativesManager.css';

function hashParams() {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.hash.split('?')[1] || '');
}

function readScreen() {
  const params = hashParams();
  const screen = params.get('screen');
  const creative = params.get('creative') || '';
  if (screen === 'types') return { screen: 'types', creative: '' };
  if (screen === 'edit' && creative) return { screen: 'edit', creative };
  return { screen: 'catalog', creative: '' };
}

function useCatalogScreen() {
  const [screen, setScreen] = useState(readScreen);
  useEffect(() => {
    const onHash = () => setScreen((current) => {
      const next = readScreen();
      return next.screen === current.screen && next.creative === current.creative ? current : next;
    });
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return screen;
}

function hashFor(params) {
  const base = window.location.hash.split('?')[0] || '#/platform/catalog';
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== '' && value != null)).toString();
  return query ? `${base}?${query}` : base;
}

function setHashQuery(params, { replace = false } = {}) {
  const target = hashFor(params);
  if (replace) replaceHash(target);
  else window.location.hash = target;
}

function Toast({ toast, onDismiss }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => {
      if (!ref.current?.contains(document.activeElement)) onDismiss();
    }, toast.action ? 8000 : 4500);
    return () => clearTimeout(timer);
  }, [toast, onDismiss]);
  // The live region is always present; the toast is inserted into it, so
  // screen readers announce each message.
  return (
    <div ref={ref} className="cm-toast-region" role="status" aria-live="polite">
      {toast ? (
        <div className="cm-toast">
          <span>{toast.message}</span>
          {toast.action ? (
            <button type="button" className="toast-undo" onClick={() => { const { onClick } = toast.action; onDismiss(); onClick(); }}>
              {toast.action.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function CreativesManager() {
  const apiClient = useMemo(() => createCreativesApi(), []);
  const { screen, creative } = useCatalogScreen();
  const [query, changeQuery] = useManagementQuery();
  const [revision, setRevision] = useState(0);
  const [toast, setToast] = useState(null);
  const catalogQuery = useRef(null);
  const [returnedToCatalog, setReturnedToCatalog] = useState(false);

  const refresh = () => setRevision((value) => value + 1);
  const showToast = (message, action = null) => setToast({ message, action, id: Date.now() });
  const dismissToast = useMemo(() => () => setToast(null), []);

  function catalogParams() {
    const params = Object.fromEntries(hashParams());
    delete params.screen;
    delete params.creative;
    return params;
  }

  // Coming from the catalog, remember its filters; a deep link keeps none.
  function rememberCatalog() {
    if (screen === 'catalog') catalogQuery.current = catalogParams();
  }

  function openCreative(id) {
    rememberCatalog();
    setHashQuery({ screen: 'edit', creative: id || 'new' });
  }

  function openTypes() {
    rememberCatalog();
    setHashQuery({ screen: 'types' });
  }

  function backToCatalog(patch = {}) {
    setReturnedToCatalog(true);
    setHashQuery({ ...(catalogQuery.current || {}), ...patch, offset: '0' });
    refresh();
  }

  let content;
  if (screen === 'types') {
    content = (
      <RenovationTypesManager
        apiClient={apiClient}
        onBack={() => backToCatalog()}
        onToast={showToast}
        onViewCreatives={(typeId) => backToCatalog({ type: typeId, status: '', search: '' })}
        onTypeDeleted={(typeId) => {
          if (catalogQuery.current?.type === typeId) catalogQuery.current = { ...catalogQuery.current, type: '' };
        }}
      />
    );
  } else if (screen === 'edit') {
    content = (
      <CreativeEditor
        key={creative}
        apiClient={apiClient}
        packageId={creative === 'new' ? null : creative}
        onBack={() => backToCatalog()}
        onToast={showToast}
        onCreated={(id) => setHashQuery({ screen: 'edit', creative: id }, { replace: true })}
        onManageTypes={openTypes}
        onRefreshCatalog={refresh}
      />
    );
  } else {
    content = (
      <CreativesCatalog
        apiClient={apiClient}
        query={query}
        onQueryChange={changeQuery}
        revision={revision}
        onRefresh={refresh}
        onToast={showToast}
        onManageTypes={openTypes}
        onOpenCreative={(row) => openCreative(row.id)}
        onAddCreative={() => openCreative(null)}
        onUnarchived={(row) => openCreative(row.id)}
        focusHeadingOnMount={returnedToCatalog}
      />
    );
  }

  return (
    <section className="platform-management creatives-manager">
      {content}
      <Toast toast={toast} onDismiss={dismissToast} />
    </section>
  );
}
